import { CreateToastFnReturn } from '@chakra-ui/react'
import { supabase } from '../../../supabaseClient'
import { EncoderEvent, SuggestedChange } from './types'

interface ActionContext {
    toast: CreateToastFnReturn
    dispatch: React.Dispatch<EncoderEvent>
}

export async function submitEncoding(
    encodingId: string,
    userId: string,
    body: string,
    context: ActionContext
): Promise<void> {
    const { toast, dispatch } = context
    const toastId = 'submit-toast'

    dispatch({ type: 'SUBMIT_STARTED' })

    try {
        const { data, error } = await supabase
            .from('etcbc_encoding_pendingapproval')
            .insert({
                encoding: encodingId,
                user_id: userId,
                body,
            })
            .select()
            .single()

        if (error) {
            throw error
        }

        dispatch({
            type: 'SUBMIT_COMPLETED',
            submission: {
                id: data.id,
                body: data.body,
                user_id: data.user_id,
            },
        })

        toast({
            id: toastId,
            title: 'Info',
            description: 'Submitted',
            status: 'success',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top',
        })
    } catch {
        dispatch({ type: 'SUBMIT_FAILED' })

        if (!toast.isActive(toastId)) {
            toast({
                id: toastId,
                title: 'Issue',
                description: 'Could not submit encoding',
                status: 'error',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'bottom',
            })
        }
    }
}

export async function updateSubmission(
    submissionId: string,
    body: string,
    context: ActionContext
): Promise<void> {
    const { toast, dispatch } = context
    const toastId = 'update-toast'

    dispatch({ type: 'UPDATE_STARTED' })

    try {
        const { error } = await supabase
            .from('etcbc_encoding_pendingapproval')
            .update({ body })
            .eq('id', submissionId)
            .select()

        if (error) {
            throw error
        }

        dispatch({ type: 'UPDATE_COMPLETED' })

        toast({
            id: toastId,
            title: 'Info',
            description: 'Update completed',
            status: 'success',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top',
        })
    } catch {
        dispatch({ type: 'UPDATE_FAILED' })

        toast({
            id: toastId,
            title: 'Issue',
            description: 'Update failed',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'bottom',
        })
    }
}

export async function cancelSubmission(
    submissionId: string,
    context: ActionContext,
    setFieldValue: (field: string, value: string) => void,
    originalBody: string
): Promise<void> {
    const { toast, dispatch } = context
    const toastId = 'cancel-toast'

    dispatch({ type: 'CANCEL_STARTED' })

    try {
        const { error } = await supabase
            .from('etcbc_encoding_pendingapproval')
            .delete()
            .eq('id', submissionId)

        if (error) {
            throw error
        }

        setFieldValue('proposedvalue', originalBody)
        dispatch({ type: 'CANCEL_COMPLETED' })

        toast({
            id: toastId,
            title: 'Info',
            description: 'Submission cancelled',
            status: 'success',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top',
        })
    } catch {
        dispatch({ type: 'CANCEL_FAILED' })

        if (!toast.isActive(toastId)) {
            toast({
                id: toastId,
                title: 'Issue',
                description: 'Could not cancel submission',
                status: 'error',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'bottom',
            })
        }
    }
}

export async function approveSubmission(
    encodingId: string,
    body: string,
    adminUserId: string,
    suggestedChange: SuggestedChange | null,
    context: ActionContext
): Promise<void> {
    const { toast, dispatch } = context
    const toastId = 'approve-toast'

    dispatch({ type: 'APPROVE_STARTED' })

    try {
        // Update the main encoding
        const { error: encodingError } = await supabase
            .from('etcbc_encoding')
            .update({ body })
            .eq('id', encodingId)

        // Delete the pending approval
        const { error: approvalError } = await supabase
            .from('etcbc_encoding_pendingapproval')
            .delete()
            .eq('encoding', encodingId)

        // Insert history records
        const { error: historyError } = await supabase
            .from('etcbc_encoding_history')
            .insert([
                {
                    action: 'encoded',
                    encoding: encodingId,
                    user_id: suggestedChange?.user_id.id,
                    changes_log: suggestedChange?.body,
                },
                {
                    action: 'approve',
                    encoding: encodingId,
                    user_id: adminUserId,
                    changes_log: body,
                },
            ])

        if (encodingError || approvalError || historyError) {
            throw new Error('Approval failed')
        }

        dispatch({ type: 'APPROVE_COMPLETED' })

        toast({
            id: toastId,
            title: 'Info',
            description: 'Submission approved',
            status: 'success',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top',
        })
    } catch {
        dispatch({ type: 'APPROVE_FAILED' })

        if (!toast.isActive(toastId)) {
            toast({
                id: toastId,
                title: 'Issue',
                description: 'Could not approve encoding',
                status: 'error',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'bottom',
            })
        }
    }
}
